import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, CircularProgress, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import api from '../../utils/functions/api';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import EmployeeAvatar, { resolveEmployeePhoto } from '../../components/EmployeeAvatar';

const LEAVE_TYPES = [
  { key: 'SICK', label: 'ลาป่วย' },
  { key: 'PERSONAL', label: 'ลากิจ' },
  { key: 'VACATION', label: 'ลาพักร้อน' },
  { key: 'MATERNITY', label: 'ลาคลอด' },
  { key: 'ORDINATION', label: 'ลาบวช' },
  { key: 'UNPAID', label: 'ลาไม่รับค่าจ้าง' },
];

const fmtDay = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
};

// ช่วงเวลา -> { dateStart, dateEnd } (ISO) หรือ null สำหรับ "ทั้งหมด"
const rangeFor = (mode, custom) => {
  if (mode === 'month') {
    return { dateStart: dayjs().startOf('month').toISOString(), dateEnd: dayjs().endOf('month').toISOString() };
  }
  if (mode === 'year') {
    return { dateStart: dayjs().startOf('year').toISOString(), dateEnd: dayjs().endOf('year').toISOString() };
  }
  if (mode === 'custom') {
    if (!custom.from || !custom.to) return undefined; // ยังกรอกไม่ครบ
    return {
      dateStart: dayjs(custom.from).startOf('day').toISOString(),
      dateEnd: dayjs(custom.to).endOf('day').toISOString(),
    };
  }
  return null; // all
};

export default function LeaveSummaryModal({ open, onClose }) {
  const [rangeMode, setRangeMode] = useState('year'); // month | year | all | custom
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const range = rangeFor(rangeMode, custom);
    if (range === undefined) return undefined; // custom ยังไม่ครบ
    let cancelled = false;
    setLoading(true);
    api
      .get(`${process.env.REACT_APP_API_URL}/leave-request`, {
        params: {
          // นับเฉพาะใบลาที่อนุมัติแล้วเท่านั้น (ลาไปจริง)
          status: 'APPROVED',
          size: 999999,
          page: 1,
          ...(range ? { dateStart: range.dateStart, dateEnd: range.dateEnd } : {}),
        },
      })
      .then(({ data }) => {
        if (!cancelled) setRows(Array.isArray(data?.rows) ? data.rows : []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, rangeMode, custom]);

  // รวมวันลาต่อพนักงาน แยกตามประเภท
  const summary = useMemo(() => {
    const byEmp = new Map();
    rows.forEach((r) => {
      const emp = r.employee || {};
      const id = String(emp._id || emp.employee_id || r.employee || 'unknown');
      if (!byEmp.has(id)) {
        byEmp.set(id, {
          id,
          name: formatEmployeeThaiName(emp) || emp.employee_id || 'ไม่ระบุ',
          employee_id: emp.employee_id || '',
          department: emp.department?.name || '',
          photo: resolveEmployeePhoto(emp),
          byType: {},
          total: 0,
          count: 0,
        });
      }
      const e = byEmp.get(id);
      const days = Number(r.total_days) || 0;
      e.byType[r.leave_type] = (e.byType[r.leave_type] || 0) + days;
      e.total += days;
      e.count += 1;
    });
    return Array.from(byEmp.values()).sort((a, b) => b.total - a.total);
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return summary;
    return summary.filter(
      (e) => e.name.toLowerCase().includes(q) || e.employee_id.toLowerCase().includes(q),
    );
  }, [summary, search]);

  const grand = useMemo(() => {
    const g = { byType: {}, total: 0, count: 0 };
    filtered.forEach((e) => {
      LEAVE_TYPES.forEach(({ key }) => { g.byType[key] = (g.byType[key] || 0) + (e.byType[key] || 0); });
      g.total += e.total;
      g.count += e.count;
    });
    return g;
  }, [filtered]);

  const rangeLabel = rangeMode === 'month'
    ? `เดือน ${dayjs().format('MMMM YYYY')}`
    : rangeMode === 'year'
      ? `ปี ${dayjs().format('YYYY')}`
      : rangeMode === 'custom'
        ? (custom.from && custom.to ? `${dayjs(custom.from).format('DD/MM/YY')} – ${dayjs(custom.to).format('DD/MM/YY')}` : 'เลือกช่วงวันที่')
        : 'ทั้งหมดในระบบ';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ py: 1.25 }}>
        สรุปการลาของพนักงาน
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {rangeLabel} · เฉพาะใบลาที่อนุมัติแล้ว
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5 }}>
        {/* ตัวกรอง + สรุปตัวเลขในแถวเดียว */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={rangeMode}
            onChange={(e, v) => v && setRangeMode(v)}
          >
            <ToggleButton value="month" sx={{ textTransform: 'none' }}>เดือนนี้</ToggleButton>
            <ToggleButton value="year" sx={{ textTransform: 'none' }}>ปีนี้</ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: 'none' }}>ทั้งหมด</ToggleButton>
            <ToggleButton value="custom" sx={{ textTransform: 'none' }}>กำหนดเอง</ToggleButton>
          </ToggleButtonGroup>

          {rangeMode === 'custom' && (
            <>
              <TextField
                size="small" type="date" label="ตั้งแต่"
                value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small" type="date" label="ถึง"
                value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          <TextField
            size="small" placeholder="ค้นหาชื่อ / รหัส"
            value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 170, flexGrow: 1 }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} /> }}
          />

          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {filtered.length} คน · รวม{' '}
            <Typography component="span" variant="body2" fontWeight={700} color="text.primary">
              {fmtDay(grand.total)}
            </Typography>{' '}
            วัน · {grand.count} ครั้ง
          </Typography>
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Box py={6} textAlign="center">
            <Typography color="text.secondary">ไม่มีข้อมูลการลาในช่วงที่เลือก</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, maxHeight: '64vh' }}>
            <Table size="small" stickyHeader sx={{ '& td, & th': { py: 0.5, px: 1 } }}>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: 'grey.50', fontWeight: 700, whiteSpace: 'nowrap' } }}>
                  <TableCell align="center" sx={{ width: 44 }}>#</TableCell>
                  <TableCell>พนักงาน</TableCell>
                  {LEAVE_TYPES.map((t) => (
                    <TableCell key={t.key} align="center">{t.label}</TableCell>
                  ))}
                  <TableCell align="center">รวม (วัน)</TableCell>
                  <TableCell align="center">ครั้ง</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((e, i) => (
                  <TableRow key={e.id} hover>
                    <TableCell align="center">
                      <Typography variant="caption" color="text.secondary">{i + 1}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <EmployeeAvatar src={e.photo} size={28} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{e.name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {[e.employee_id, e.department].filter(Boolean).join(' · ') || '—'}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    {LEAVE_TYPES.map((t) => (
                      <TableCell key={t.key} align="center">
                        {e.byType[t.key]
                          ? <Typography variant="body2">{fmtDay(e.byType[t.key])}</Typography>
                          : <Typography variant="body2" color="text.disabled">–</Typography>}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={700}>{fmtDay(e.total)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">{e.count}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ '& td': { bgcolor: 'grey.100', fontWeight: 700, position: 'sticky', bottom: 0 } }}>
                  <TableCell />
                  <TableCell>รวมทั้งสิ้น</TableCell>
                  {LEAVE_TYPES.map((t) => (
                    <TableCell key={t.key} align="center">
                      {grand.byType[t.key] ? fmtDay(grand.byType[t.key]) : '–'}
                    </TableCell>
                  ))}
                  <TableCell align="center">{fmtDay(grand.total)}</TableCell>
                  <TableCell align="center">{grand.count}</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>ปิด</Button>
      </DialogActions>
    </Dialog>
  );
}
